import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface WelcomeModalProps {
  isOpen: boolean;
  onComplete: (data: WelcomeData) => void;
}

export interface WelcomeData {
  email: string;
  mobile: string;
  region: string;
  language: string;
}

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia',
  'Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland',
  'Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey',
  'New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina',
  'South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];
const CA_PROVINCES = [
  'Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador',
  'Nova Scotia','Ontario','Prince Edward Island','Quebec','Saskatchewan',
  'Northwest Territories','Nunavut','Yukon',
];
const REGIONS = [
  { group: 'United States', items: US_STATES },
  { group: 'Canada', items: CA_PROVINCES },
];

export function WelcomeModal({ isOpen, onComplete }: WelcomeModalProps) {
  const [step, setStep] = useState<'welcome' | 'setup'>('welcome');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [region, setRegion] = useState('');
  const [language, setLanguage] = useState('en');

  const handleSubmit = () => {
    onComplete({ email, mobile, region, language });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[3000] bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[3001] flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>

              {step === 'welcome' ? (
                <>
                  {/* Logo + Welcome */}
                  <div className="px-6 pt-8 pb-2 text-center">
                    <img src="/wfg-logo.svg" alt="WFG" className="h-10 mx-auto mb-5" />
                    <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                      Welcome to the New WFG Dashboard
                    </h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
                      Early access — MVP Preview
                    </p>
                  </div>

                  <div className="px-6 pb-6 space-y-4">
                    {/* What it is */}
                    <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                      <h3 className="text-sm font-bold text-primary-700 dark:text-primary-300 mb-1">✅ What's available now</h3>
                      <ul className="text-sm text-primary-600 dark:text-primary-400 space-y-1 list-disc list-inside">
                        <li>Commissions detail with filtering and views</li>
                        <li>Book of Business with policy details</li>
                        <li>Points tracking and team breakdown</li>
                      </ul>
                    </div>

                    {/* What it isn't */}
                    <div className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600">
                      <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">🚧 Not yet available</h3>
                      <ul className="text-sm text-neutral-500 dark:text-neutral-400 space-y-1 list-disc list-inside">
                        <li>Main dashboard landing page</li>
                        <li>Reports and export functionality</li>
                        <li>Hierarchy visualization</li>
                        <li>Notifications and alerts</li>
                      </ul>
                    </div>

                    {/* What's coming */}
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <h3 className="text-sm font-bold text-amber-700 dark:text-amber-300 mb-1">🔜 Coming soon</h3>
                      <ul className="text-sm text-amber-600 dark:text-amber-400 space-y-1 list-disc list-inside">
                        <li>Personalized dashboard with widgets</li>
                        <li>Real-time notifications</li>
                        <li>Advanced reporting and exports</li>
                        <li>Mobile-optimized experience</li>
                      </ul>
                    </div>

                    <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center pt-2">
                      Your feedback shapes what we build next. Use the feedback button anytime.
                    </p>

                    <button
                      onClick={() => setStep('setup')}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors text-sm"
                    >
                      Get Started →
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Setup Step */}
                  <div className="px-6 pt-6 pb-2">
                    <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-1">Set up your profile</h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-5">You can change these anytime from the menu.</p>
                  </div>

                  <div className="px-6 pb-6 space-y-4">
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1">
                        Email address <span className="text-neutral-400 font-normal">(for 2FA verification)</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-colors"
                      />
                    </div>

                    {/* Mobile */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1">
                        Mobile number <span className="text-neutral-400 font-normal">(optional, for SMS alerts)</span>
                      </label>
                      <input
                        type="tel"
                        value={mobile}
                        onChange={e => setMobile(e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-colors"
                      />
                    </div>

                    {/* Region */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1">Region</label>
                      <select
                        value={region}
                        onChange={e => setRegion(e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-colors"
                      >
                        <option value="">Select your state or province</option>
                        {REGIONS.map(group => (
                          <optgroup key={group.group} label={group.group}>
                            {group.items.map(item => (
                              <option key={item} value={item}>{item}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    {/* Language */}
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1">Language</label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setLanguage('en')}
                          className={clsx(
                            'flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all',
                            language === 'en'
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'border-neutral-200 dark:border-neutral-600 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300'
                          )}
                        >
                          🇺🇸 English
                        </button>
                        <button
                          onClick={() => setLanguage('fr')}
                          className={clsx(
                            'flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all',
                            language === 'fr'
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'border-neutral-200 dark:border-neutral-600 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300'
                          )}
                        >
                          🇨🇦 Français
                        </button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => setStep('welcome')}
                        className="px-4 py-2.5 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                      >
                        ← Back
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={!email.trim()}
                        className={clsx(
                          'flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all',
                          email.trim()
                            ? 'bg-primary-500 hover:bg-primary-600 text-white'
                            : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 cursor-not-allowed'
                        )}
                      >
                        Complete Setup
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
